const MAX_MOUSE_EVENT_DELTA = 200;
const MAX_MOUSE_FRAME_DELTA = 120;
const MAX_VIRTUAL_LOOK_FRAME_RADIANS = 2.6;

export function filterMouseEventDelta(value: number): number {
  return Number.isFinite(value) && Math.abs(value) <= MAX_MOUSE_EVENT_DELTA ? value : 0;
}

export function clampMouseFrameDelta(value: number): number {
  return Math.max(-MAX_MOUSE_FRAME_DELTA, Math.min(MAX_MOUSE_FRAME_DELTA, value));
}

export function clampVirtualLookRadians(value: number): number {
  return Math.max(-MAX_VIRTUAL_LOOK_FRAME_RADIANS, Math.min(MAX_VIRTUAL_LOOK_FRAME_RADIANS, value));
}

export class InputManager {
  private readonly heldKeys = new Set<string>();
  private readonly pressedKeys = new Set<string>();
  private mouseDeltaX = 0;
  private mouseDeltaY = 0;
  private wheelDirection = 0;
  private firePressed = false;
  private fireHeld = false;
  private virtualFireHeld = false;
  private virtualSprint = false;
  private enabled = false;
  private virtualMoveX = 0;
  private virtualMoveZ = 0;
  private virtualLookX = 0;
  private virtualLookY = 0;
  private readonly disposers: Array<() => void> = [];

  public constructor(private readonly canvas: HTMLCanvasElement) {
    this.listen(window, "keydown", (event: KeyboardEvent) => {
      if (!this.enabled) return;
      if (!this.heldKeys.has(event.code)) {
        this.pressedKeys.add(event.code);
      }
      this.heldKeys.add(event.code);
      if (["Tab", "Space", "ArrowUp", "ArrowDown"].includes(event.code)) {
        event.preventDefault();
      }
    });
    this.listen(window, "keyup", (event: KeyboardEvent) => {
      this.heldKeys.delete(event.code);
    });
    this.listen(window, "mousemove", (event: MouseEvent) => {
      if (document.pointerLockElement === this.canvas) {
        this.mouseDeltaX += filterMouseEventDelta(event.movementX);
        this.mouseDeltaY += filterMouseEventDelta(event.movementY);
      }
    });
    this.listen(document, "pointerlockchange", () => this.clearMouseDelta());
    this.listen(this.canvas, "mousedown", (event: MouseEvent) => {
      if (this.enabled && document.pointerLockElement === this.canvas && event.button === 0) {
        this.firePressed = true;
        this.fireHeld = true;
      }
    });
    this.listen(window, "mouseup", (event: MouseEvent) => {
      if (event.button === 0) {
        this.fireHeld = false;
      }
    });
    this.listen(this.canvas, "wheel", (event: WheelEvent) => {
      if (!this.enabled) return;
      this.wheelDirection = Math.sign(event.deltaY);
      event.preventDefault();
    });
    this.listen(window, "blur", () => this.clear());
  }

  public isHeld(code: string): boolean {
    return this.heldKeys.has(code) || (code === "ShiftLeft" && this.virtualSprint);
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.clear();
  }

  public setVirtualSprint(held: boolean): void {
    this.virtualSprint = held;
  }

  public get movementAxis(): Readonly<{ x: number; z: number }> {
    return {
      x: Math.max(-1, Math.min(1, this.virtualMoveX)),
      z: Math.max(-1, Math.min(1, this.virtualMoveZ)),
    };
  }

  public setVirtualMovement(x: number, z: number): void {
    this.virtualMoveX = x;
    this.virtualMoveZ = z;
  }

  public addVirtualLook(x: number, y: number): void {
    this.virtualLookX += x;
    this.virtualLookY += y;
  }

  public pressVirtualKey(code: string): void {
    this.pressedKeys.add(code);
  }

  public setVirtualFire(held: boolean): void {
    if (held && !this.virtualFireHeld) this.firePressed = true;
    this.virtualFireHeld = held;
  }

  public consumePressed(code: string): boolean {
    const pressed = this.pressedKeys.has(code);
    this.pressedKeys.delete(code);
    return pressed;
  }

  public consumeLookDelta(mouseSensitivity: number): Readonly<{ x: number; y: number }> {
    const delta = {
      x:
        clampMouseFrameDelta(this.mouseDeltaX) * mouseSensitivity +
        clampVirtualLookRadians(this.virtualLookX),
      y:
        clampMouseFrameDelta(this.mouseDeltaY) * mouseSensitivity +
        clampVirtualLookRadians(this.virtualLookY),
    };
    this.clearMouseDelta();
    this.virtualLookX = 0;
    this.virtualLookY = 0;
    return delta;
  }

  public consumeWheelDirection(): number {
    const direction = this.wheelDirection;
    this.wheelDirection = 0;
    return direction;
  }

  public consumeFirePressed(): boolean {
    const pressed = this.firePressed;
    this.firePressed = false;
    return pressed;
  }

  public get isFireHeld(): boolean {
    return this.fireHeld || this.virtualFireHeld;
  }

  public requestPointerLock(): void {
    try {
      const request = this.canvas.requestPointerLock();
      if (request instanceof Promise) void request.catch(() => undefined);
    } catch {
      // Pointer lock can be unavailable in embedded or automated browsers.
    }
  }

  public clear(): void {
    this.heldKeys.clear();
    this.pressedKeys.clear();
    this.firePressed = false;
    this.fireHeld = false;
    this.clearMouseDelta();
    this.wheelDirection = 0;
    this.virtualMoveX = 0;
    this.virtualMoveZ = 0;
    this.virtualLookX = 0;
    this.virtualLookY = 0;
    this.virtualFireHeld = false;
    this.virtualSprint = false;
  }

  public dispose(): void {
    for (const dispose of this.disposers.splice(0)) {
      dispose();
    }
    this.clear();
  }

  private listen<Target extends EventTarget, EventName extends string, EventType extends Event>(
    target: Target,
    type: EventName,
    listener: (event: EventType) => void,
  ): void {
    const eventListener = listener as EventListener;
    target.addEventListener(type, eventListener);
    this.disposers.push(() => target.removeEventListener(type, eventListener));
  }

  private clearMouseDelta(): void {
    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;
  }
}
